import { getAtsJobCreate } from '@/store/atsJobCreate';
import { selectCurrUserPermission } from '@/store/bootstrap/selectors';
import { usePermission } from '@/hooks/usePermission';
import { useSelector } from 'react-redux';
import { useAuth } from './use-auth';
import { useIsExternalUser } from './useIsExternalUser';

export const useAtsPermissions = () => {
  const { currUser = {} } = useAuth();
  const { role: currUserRole, id: currUserId, email } = currUser || {};
  const isCurrUserAdmin = currUserRole === 'admin';
  const isExternalUser = useIsExternalUser();
  const isCreateAllowed = ['owner', 'admin']?.includes(currUserRole);
  const permissions = useSelector(selectCurrUserPermission) || {};
  const isFlowEnabled = currUser?.outreach_enabled;

  const { currentJob = {} } = useSelector(getAtsJobCreate);
  const {
    data: {
      can_edit = false,
      can_edit_offer = false,
      status = undefined,
      can_view_all_job_information = false,
    } = {},
  } = currentJob;

  const isJobUpdateAllowedStatus = !['closed', 'archived']?.includes(status);

  const isJobCreateAllowed = usePermission({
    permission: 'api_ats_v1_ats_job.create?',
  });
  const isJobsVisible = usePermission({
    permission: 'api_ats_v1_ats_job.index?',
  });
  const isReportsEnabledForAtsUsers = usePermission({
    // todo later add condition for custom reports
    permission: 'api_ats_v1_reports_hires.card?',
  });

  const requisitionsPermissions = {
    index: permissions?.['api_ats_v1_requisition.index?'],
    create: permissions?.['api_ats_v1_requisition.create?'],
  };

  const jobPermissions = {
    create: permissions?.['api_ats_v1_ats_job.create?'],
    update: can_edit && isJobUpdateAllowedStatus,
    isJobUpdateAllowedStatus,
    offer: can_edit_offer,
    index: permissions?.['api_ats_v1_ats_job.index?'],
    jobStatusUpdate: can_edit && status !== 'archived',
    canViewJobInfo: can_view_all_job_information,
    can_edit,
  };

  const flowsPermissions = {
    addCandidate: permissions?.['outreach.add_candidate?'],
    show: permissions?.['outreach.show?'],
    createFlow: permissions?.['outreach.create?'],
    cloneFlow: permissions?.['outreach.clone?'],
    launchFlow: permissions?.['outreach.activate?'],
    archiveFlow: permissions?.['outreach.archive?'],
    blueprintOrg: permissions?.['outreach_blueprint.org?'],
    completeFlow: permissions?.['outreach.complete?'],
    pauseFlow: permissions?.['outreach.pause?'],
    accessOtherUsersFlows: permissions?.['outreach.access_other_users_flows?'],
    index: permissions?.['outreach.index?'],
  };

  const projectsPermissions = {
    create: permissions?.['project.create?'], // create project
    add_prospect: permissions?.['project.add_prospect?'], // add candidate
    archive: permissions?.['project.archive?'], // archive project
    clone: permissions?.['project.clone?'], //clone project
    create_review: permissions?.['project.create_review?'], // create a review
    index: permissions?.['project.index?'], //list screen
    internal_users: permissions?.['project.internal_users?'], // displaying viewers and editors
    list_prospects: permissions?.['project.list_prospects?'], // candidate list
    remove_prospect: permissions?.['project.remove_prospect?'], // remove form the project
    update: permissions?.['project.update?'], // update users
  };

  const contactsPermissions = {
    create: permissions?.['contact.create?'],
    index: permissions?.['circles.contacts?'],
  };

  return {
    isJobsVisible,
    isCreateAllowed,
    isJobCreateAllowed,
    currUserId,
    email,
    currUserRole,
    isExternalUser,
    isCurrUserAdmin,
    // need to start integrating based on this permission
    permissions,
    jobPermissions,
    isReportsEnabledForAtsUsers,
    flowsPermissions,
    contactsPermissions,
    projectsPermissions,
    isFlowEnabled,
    requisitionsPermissions,
  };
};
